import React, { HTMLProps } from 'react';
import styled, { FlattenInterpolation, FlattenSimpleInterpolation, ThemeProps } from 'styled-components';
import spinner from '@carvana/showroom-spinner';
import '@carvana/showroom-css-theme/Theme.css';
import '@carvana/showroom-spinner/index.css';
import PrimaryButton from '@carvana/showroom-forms/Button/Primary';
import SecondaryButton from '@carvana/showroom-forms/Button/Secondary';
import TertiaryButton from '@carvana/showroom-forms/Button/Tertiary';
import useLoadTimeTracking from '../hooks/useLoadTimeTracking';

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;

  @media (min-width: ${props => props.theme.breakpoint.md}px) {
    justify-content: flex-start;
  }
`;

const ButtonLoadingSpinner = styled(spinner)``;

const LoadTrackingSpinner: React.FC<React.ComponentProps<
  typeof ButtonLoadingSpinner
>> = props => {
  useLoadTimeTracking();
  return <ButtonLoadingSpinner {...props} />;
};

type StyleProps = {
  uppercase: boolean;
  fontSize: number;
};

const ButtonBase = styled(PrimaryButton)<StyleProps>`
  text-transform: ${props => (props.uppercase ? 'uppercase' : 'none')};
  width: auto;
  ${props =>
    props.fontSize >= 0
      ? `font-size: ${props.fontSize}px;`
      : 'font-size: 14px;'}
`;

const SecondaryButtonBase = styled(SecondaryButton)<StyleProps>`
  text-transform: ${props => (props.uppercase ? 'uppercase' : 'none')};
  width: auto;
  ${props =>
    props.fontSize >= 0
      ? `font-size: ${props.fontSize}px;`
      : 'font-size: 14px;'}
`;

const TertiaryButtonBase = styled(TertiaryButton)<StyleProps>`
  text-transform: ${props => (props.uppercase ? 'uppercase' : 'none')};
  width: auto;
  ${props =>
    props.fontSize >= 0
      ? `font-size: ${props.fontSize}px;`
      : 'font-size: 14px;'}
`;

export const CarvanaButtonNoCapsV2 = styled(PrimaryButton)<StyleProps>`
  font-weight: 500;
  text-transform: none;
`;

interface Props extends HTMLProps<typeof ButtonBase> {
  mode?: string;
  isLoading?: boolean;
  text?: string;
  uppercase?: boolean;
  buttonStyles?: string | FlattenSimpleInterpolation | FlattenInterpolation<ThemeProps<any>> | null;
  buttonWrapperStyles?: string | FlattenSimpleInterpolation | null;
  loadingSpinnerStyles?: string | FlattenSimpleInterpolation | null;
  fontSize?: number | null;
  secondary?: boolean;
  tertiary?: boolean;
}

const defaultProps: Props = {
  children: null,
  mode: 'light',
  disabled: false,
  isLoading: false,
  fontSize: null,
  text: '',
  uppercase: false,
  buttonStyles: null,
  buttonWrapperStyles: null,
  loadingSpinnerStyles: null,
  secondary: false,
  tertiary: false
};

const CarvanaButtonV2: React.FC<Props> = ({
  type,
  children,
  mode,
  disabled,
  isLoading,
  onClick,
  text,
  uppercase,
  buttonStyles,
  buttonWrapperStyles,
  loadingSpinnerStyles,
  fontSize,
  secondary,
  tertiary
} = defaultProps) => {
  const buttonDefaultProps = {
    css: buttonStyles,
    disabled,
    fontSize,
    mode,
    onClick,
    type,
    uppercase,
    secondary,
    tertiary
  };

  return (
    <ButtonWrapper css={buttonWrapperStyles}>
      {isLoading && <LoadTrackingSpinner css={loadingSpinnerStyles} />}
      {!isLoading && !secondary && !tertiary && (
        <ButtonBase {...buttonDefaultProps} css={buttonStyles}>
          {text || children}
        </ButtonBase>
      )}
      {!isLoading && secondary && !tertiary && (
        <SecondaryButtonBase {...buttonDefaultProps} css={buttonStyles}>
          {text || children}
        </SecondaryButtonBase>
      )}
      {!isLoading && tertiary && !secondary && (
        <TertiaryButtonBase {...buttonDefaultProps} css={buttonStyles}>
          {text || children}
        </TertiaryButtonBase>
      )}
    </ButtonWrapper>
  );
};

export default CarvanaButtonV2;
